import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Subject } from 'rxjs';
import { requireCheckboxesToBeCheckedValidator } from 'src/app/Modules/idms/core/atleastOneChecked.validator';
import { UniqueNameValidation } from 'src/app/Modules/management/core/Validators/unique-name.validator';
import { LineNumberFormat, LineNumberFormatSection, LineNumberFormatSectionType } from 'src/app/Modules/management/models/instance/line-number-format.model';
import { CompareNumbers } from 'src/app/Modules/settings/core/Validators/compare.Validator';
import { ManagementApiService } from 'src/app/services';
import { CustomToastService } from 'src/app/services/custom-toast.service';
import { InstanceDataService } from 'src/app/services/instance-data.service';
import { UserUtilityService } from 'src/app/services/user-utility.service';
import { DeleteDialogComponent } from 'src/app/shared/info-dialogs/delete-dialog/delete-dialog.component';
import { userActions } from 'src/app/models/enums/user-permissions.enum';
import { SettingArea } from 'src/app/Modules/settings/models/common/setting-area.model';
import { InstanceSettings } from '../../../../models/instance/instace-setting.model';
import { AppConstants } from 'src/app/constants/appconstants';
import { SuccessDialogComponent } from 'src/app/shared/info-dialogs/success-dialog/success-dialog.component';
import { takeUntil } from 'rxjs/operators';
import { NavigateUrlService } from 'src/app/services/navigateurl.service';

@Component({
    selector: 'app-instance-others',
    templateUrl: './instance-others.component.html',
    styleUrls: ['./instance-others.component.scss'],
    standalone: false
})
export class InstanceOthersComponent implements OnInit, OnDestroy {
    defaultLineNumberFormatList: LineNumberFormat[];
    selectedLineFormat: LineNumberFormat;
    defaultLineNumberFormatStatusType: LineNumberFormatSectionType[];
    lineNumberFormatForm: UntypedFormGroup;
    lineNumberFormatNameList: string[];
    apiPayLoad: LineNumberFormat[];
    destroy$: Subject<any>;
    currentInstanceId: string;
    formGroupArray: FormGroup[];
    activeIndex: number;
    isViewPermission: boolean;
    jointFormatList = ['Default'];
    isJointFormatSelect: boolean;
    settingArea: SettingArea;
    jointIDForm: UntypedFormGroup;
    jointIDSettings: InstanceSettings;
    JointIdDropDown: InstanceSettings;
    selectedLineNumberIndex: number;

    constructor(
        private ngbModal: NgbModal,
        private instanceDataService: InstanceDataService,
        private managementApiService: ManagementApiService,
        private ngxUiLoaderService: NgxUiLoaderService,
        private userUtilityService: UserUtilityService,
        private fromBuilder: UntypedFormBuilder,
        private activatedRoute: ActivatedRoute,
        private customToastService: CustomToastService,
        private cd: ChangeDetectorRef,
        private router: Router,
        private user_utility: UserUtilityService,
        private navigateUrlService: NavigateUrlService
    ) {
        this.destroy$ = new Subject();
        this.formGroupArray = [];
        this.apiPayLoad = [];
        this.selectedLineFormat = null;
        this.isViewPermission = false;
        this.isJointFormatSelect = false;
    }

    async ngOnInit(): Promise<void> {
        const canLoad = await this.userUtilityService.checkUserPermissionDataAndRoute('viewLineNumberFormat', userActions.View);

        this.instanceDataService.instanceActiveTab.pipe(takeUntil(this.destroy$)).subscribe(async (res) => {
            if (res == AppConstants.tab_numberFormat && canLoad) {
                this.currentInstanceId = this.instanceDataService.instanceDetails.instanceId;

                await this.initializeComponent();
                await this.getJointIDSettingArea();
            }
        });

        this.instanceDataService.instanceTabNext.pipe(takeUntil(this.destroy$)).subscribe((resp) => {
            if (resp == AppConstants.tab_numberFormat) {
                this.saveLineNumberFormatChanges();
            }
        });

        this.instanceDataService.instanceTabBack.pipe(takeUntil(this.destroy$)).subscribe((resp) => {
            if (resp == AppConstants.tab_numberFormat) {
                this.navigateBack();
            }
        });

        this.instanceDataService.lineNumberFormArray = [...this.formGroupArray];
    }

    // * initialize component
    async initializeComponent() {
        await this.getLineNumberFormatSectionTypes();
        if (this.activatedRoute.snapshot.paramMap.get('id')) {
            // * instance update mode
            this.currentInstanceId = this.activatedRoute.snapshot.paramMap.get('id');
            await this.getLineNumberFormatByInstanceId();
            // await  this.getJointIDSettingArea();
        } else {
            // * instance create mode
            await this.getDefaultLineNumberTemplate();
            // this.getJointIDSettingArea();
        }

        this.jointIDForm = this.fromBuilder.group({
            jointIdType: new UntypedFormControl('', Validators.required),
            jointIDPrefix: new UntypedFormControl('', []),
        });
    }

    // * api call to get the default template/list
    async getDefaultLineNumberTemplate() {
        try {
            if (!this.defaultLineNumberFormatList?.length) {
                this.ngxUiLoaderService.start();
                let result = await this.managementApiService.getLineNumberTemplate().toPromise();

                this.ngxUiLoaderService.stop();
                if (result.success) {
                    this.defaultLineNumberFormatList = result.response;
                    this.formGroupArray = [];
                    this.defaultLineNumberFormatList.forEach((item) => {
                        this.formGroupArray.push(this.createFormControls(item));
                    });

                    this.formGroupArray.forEach((group) => {
                        group.markAsDirty();
                    });
                    this.formGroupArray.forEach((group) => {
                        group.valueChanges.subscribe((res) => {
                            this.updateValidators();
                        });
                    });

                    this.instanceDataService.lineNumberFormArray = this.formGroupArray;
                } else if (result.isCustom) {
                    this.userUtilityService.errorModelPopup(result.message);
                }
            }
        } catch (e) {
            this.ngxUiLoaderService.stop();
        }
    }

    // * api call to get the default line section format types
    async getLineNumberFormatSectionTypes() {
        try {
            if (!this.defaultLineNumberFormatStatusType?.length) {
                this.ngxUiLoaderService.start();
                let result = await this.managementApiService.getLineNumberFormatSectionTypes().toPromise();

                if (result.success) {
                    this.ngxUiLoaderService.stop();
                    this.defaultLineNumberFormatStatusType = result.response;
                } else if (result.isCustom) {
                    this.userUtilityService.errorModelPopup(result.message);
                }
            }
        } catch (e) {
            this.ngxUiLoaderService.stop();
        }
    }

    // * api call to get the line number format by instance id
    async getLineNumberFormatByInstanceId() {
        if (!this.defaultLineNumberFormatList?.length) {
            this.getLineNumberFormatByInstanceIdAPIcall();
        }
    }

    async getLineNumberFormatByInstanceIdAPIcall() {
        let instanceId = this.activatedRoute.snapshot.paramMap.get('id') || this.currentInstanceId;
        if (instanceId) {
            this.ngxUiLoaderService.start();
            let result = await this.managementApiService.getLineNumberFormatByInstance(instanceId).toPromise();

            if (result.success) {
                let temp = this.selectedLineFormat;
                this.selectedLineFormat = null;
                if (result.response.length) {
                    this.defaultLineNumberFormatList = result.response;
                    this.formGroupArray = [];

                    this.defaultLineNumberFormatList.forEach((item) => {
                        this.formGroupArray.push(this.createFormControls(item));
                    });

                    this.instanceDataService.lineNumberFormArray = this.formGroupArray;

                    this.formGroupArray.forEach((group) => {
                        group.valueChanges.subscribe((res) => {
                            this.updateValidators();
                        });
                    });

                    if (temp)
                        this.selectedLineFormat = this.defaultLineNumberFormatList.find((item) => {
                            return (
                                item.lineNumberFormatId == temp.lineNumberFormatId ||
                                item.name.toLocaleLowerCase().replace(' ', '') == temp.name.toLocaleLowerCase().replace(' ', '')
                            );
                        });
                    this.ngxUiLoaderService.stop();
                } else {
                    this.ngxUiLoaderService.stop();
                    this.getDefaultLineNumberTemplate();
                }
            } else if (result.isCustom) {
                this.ngxUiLoaderService.stop();
                this.userUtilityService.errorModelPopup(result.message);
            }
        }
    }
    // * api call to get the instance settings for "IDMS"
    async getJointIDSettingArea() {
        try {
            if (!this.settingArea) {
                this.ngxUiLoaderService.start();
                let res = await this.managementApiService.getSettingProfileAreaId('iDMS').toPromise();

                if (res.success) {
                    this.settingArea = res.response;
                    await this.getInstanceSettings(); // * getting JOINT ID settings
                    this.ngxUiLoaderService.stop();
                } else if (res.isCustom) {
                    this.ngxUiLoaderService.stop();
                    this.userUtilityService.errorModelPopup(res.message);
                }
            } else {
                await this.getInstanceSettings();
            }
        } catch (e) {
            this.ngxUiLoaderService.stop();
        }
    }

    // * api call to retrieve joint ID settings
    async getInstanceSettings() {
        try {
            this.ngxUiLoaderService.start();
            let res = await this.managementApiService.getInstanceSettings(this.settingArea.settingAreaId, this.currentInstanceId).toPromise();

            if (res.success) {
                //this.isJointFormatSelect = false;
                this.JointIdDropDown = res.response.find((setting) => setting.setting.key === 'JointID');
                this.jointIDSettings = res.response.find((setting) => setting.setting.key === 'JointIDPrefix');
                this.createJointIdForm(this.JointIdDropDown);
                this.ngxUiLoaderService.stop();
            } else if (res.isCustom) {
                this.ngxUiLoaderService.stop();
                this.userUtilityService.errorModelPopup(res.message);
            }
        } catch (e) {
            this.ngxUiLoaderService.stop();
        }
    }

    // * api call to save the changes
    async saveLineNumberFormatChanges() {
        let LineNumApi;
        let jointId;
        if (this.jointIDForm.dirty) {
            this.ngxUiLoaderService.start();
            jointId = await this.saveJointIdSettings();
            this.ngxUiLoaderService.stop();
        }
        this.generateApiPayload();
        if (this.apiPayLoad?.length > 0) {
            this.ngxUiLoaderService.start();
            LineNumApi = await this.saveLineNumFormerApiCall();
            this.ngxUiLoaderService.stop();
        }
        if (LineNumApi?.success || jointId?.success) {
            if (LineNumApi?.success) {
                this.formGroupArray.map((x) => {
                    if (x) {
                        x.markAsPristine();
                        x.markAsUntouched();
                    }
                });
            }
            if (jointId?.success) {
                this.jointIDForm.reset(this.jointIDForm.value);
                await this.getInstanceSettings();
            }
            let selectedurl = '';
            if (!(localStorage.getItem('selectedURL') == null || localStorage.getItem('selectedURL') == '')) {
                selectedurl = localStorage.getItem('selectedURL');
            }

            let dialogRef = this.ngbModal.open(SuccessDialogComponent, {
                size: 'sm',
                keyboard: false,
                backdrop: 'static',
            });

            dialogRef.componentInstance.message = 'Instance updated successfully';
            dialogRef.componentInstance.buttonText = 'Return to Home';
            dialogRef.result.then((res) => {
                if (res) {
                    if (!localStorage.getItem('currentInstanceName')) {
                        localStorage.setItem('currentInstanceName', this.instanceDataService?.instanceDetails?.name);
                        localStorage.setItem('currentAssetType', this.instanceDataService?.instanceDetails_New?.instanceAssetType.name);
                        this.navigateUrlService.getInstancesByOrgId(localStorage.getItem('selectedOrgId'), this.instanceDataService?.instanceDetails?.name);
                    } else {
                        this.router.navigate(['/home']);
                    }
                } else {
                    this.apiPayLoad = [];
                    this.getLineNumberFormatByInstanceIdAPIcall();
                }
            });
        } else if (LineNumApi?.isCustom) {
            this.userUtilityService.errorModelPopup(LineNumApi.message);
        } else if (jointId?.isCustom) {
            this.userUtilityService.errorModelPopup(jointId.message);
        }
    }

    async saveLineNumFormerApiCall() {
        let instanceId = this.activatedRoute.snapshot.paramMap.get('id') || this.currentInstanceId;
        return await this.managementApiService.saveLineNumberFormat(instanceId, this.apiPayLoad).toPromise();
    }

    // * api call to save the JOINT ID Settings
    async saveJointIdSettings() {
        this.ngxUiLoaderService.start();
        let finalpayload = [];

        let jointIdType = this.jointIDForm.value.jointIdType;
        let settings = this.JointIdDropDown.setting.settingValues.find((x) => x.settingValueId == jointIdType);
        let basePayload = {
            instanceId: this.currentInstanceId,
            settingId: settings.settingId || this.jointIDSettings.setting.settingId,
            instanceSettingId: this.JointIdDropDown.instanceSettingId,
            settingValueId: jointIdType,
        };

        finalpayload.push(basePayload);

        if (settings.abbreviation === 'Auto') {
            let jointSettingPrefix = {
                instanceId: this.currentInstanceId,
                settingId: this.jointIDSettings.setting.settingId,
                instanceSettingId: this.jointIDSettings.instanceSettingId,
                value: this.jointIDForm.value.jointIDPrefix,
            };
            finalpayload.push(jointSettingPrefix);
        }

        return await this.managementApiService.updateInstanceSettings(finalpayload).toPromise();
    }

    // * helper method to load the number format details
    loadNumberFormatDetails(lineNumberFormat: LineNumberFormat, index: number) {
        this.activeIndex = lineNumberFormat.sections.length - lineNumberFormat.sections.length;
        this.selectedLineFormat = lineNumberFormat;
        this.selectedLineNumberIndex = index;
        this.updateValidators();
        this.updateFromGroupValidators(index);
        this.getFormGroup();
        this.isJointFormatSelect = false;
    }

    // * helper method to create fom controls
    createFormControls(lineNumberFormat: LineNumberFormat) {
        this.lineNumberFormatNameList = this.defaultLineNumberFormatList.map((format) => {
            return format.name;
        });
        let sectionNameList = lineNumberFormat.sections.map((item) => item.name);
        let form = this.fromBuilder.group({
            lineNumberFormatId: new UntypedFormControl(lineNumberFormat?.lineNumberFormatId, []),
            name: new UntypedFormControl(lineNumberFormat.name ? lineNumberFormat.name : '', [Validators.required]),
            defaultCheck: new UntypedFormControl(lineNumberFormat.isDefault ? lineNumberFormat.isDefault : false, []),
            allowFreeCheck: new UntypedFormControl(lineNumberFormat.allowFreeForm ? lineNumberFormat.allowFreeForm : false, []),
            section: this.fromBuilder.array([]),
            exampleName: this.fromBuilder.array([]),
        });

        let sectionFormArray = form.get('section') as UntypedFormArray;
        let exampleFormArray = form.get('exampleName') as UntypedFormArray;

        lineNumberFormat.sections.forEach((section, index) => {
            let individualSectionNameList = [];
            if (section?.lineNumberFormatSectionId) {
                individualSectionNameList = sectionNameList.filter(
                    (item) => section.name.toLowerCase().replace(' ', '') !== item.toLowerCase().replace(' ', '')
                );
            } else {
                individualSectionNameList = [];
            }

            sectionFormArray.push(
                new UntypedFormGroup({
                    lineNumberFormatSectionId: new UntypedFormControl(section?.lineNumberFormatSectionId, []),
                    index: new UntypedFormControl(index + 1, []),
                    name: new UntypedFormControl(section?.name ? section?.name : '', [Validators.required, UniqueNameValidation(individualSectionNameList)]),
                    minMaxGroup: new UntypedFormGroup(
                        {
                            minChar: new UntypedFormControl(section?.minLength, [
                                Validators.required,
                                Validators.min(section?.type?.index == 5 ? 0 : 1),
                                Validators.max(12),
                            ]),
                            maxChar: new UntypedFormControl(section?.maxLength, [Validators.required, Validators.min(1), Validators.max(12)]),
                        },
                        CompareNumbers()
                    ),
                    checkboxGroup: new UntypedFormGroup(
                        {
                            allowAlpha: new UntypedFormControl(section?.allowAlpha ? section?.allowAlpha : false, []),
                            allowNumeric: new UntypedFormControl(section?.allowNumeric ? section?.allowNumeric : false, []),
                        },
                        requireCheckboxesToBeCheckedValidator()
                    ),
                    sectionType: new UntypedFormControl(
                        section?.type?.lineNumberFormatSectionTypeId || section.lineNumberFormatSectionTypeId,
                        Validators.required
                    ),
                })
            );
            exampleFormArray.push(
                new UntypedFormControl(
                    {
                        value: this.lineNumberFormatGeneration(
                            section?.allowAlpha && section?.allowNumeric ? 3 : section?.allowAlpha ? 1 : 2,
                            section?.maxLength
                        ),
                        disabled: true,
                    },
                    []
                )
            );
        });

        return form;
    }

    // * helper method to generate the random strings
    lineNumberFormatGeneration(stringType: number, stringLength: number) {
        if (!stringLength) return '';
        let result = '';
        switch (stringType) {
            case 1: // * allow alpha only
                result = Array.apply(0, Array(stringLength))
                    .map(function () {
                        return (function (charset) {
                            return charset.charAt(Math.floor(Math.random() * charset.length));
                        })('AB');
                    })
                    .join('');
                break;

            case 2: // * allow number only
                result = Array.apply(0, Array(stringLength))
                    .map(function () {
                        return (function (charset) {
                            return charset.charAt(Math.floor(Math.random() * charset.length));
                        })('12');
                    })
                    .join('');
                break;

            case 3: // * allow alpha numeric
                result = Array.apply(0, Array(stringLength))
                    .map(function () {
                        return (function (charset) {
                            return charset.charAt(Math.floor(Math.random() * charset.length));
                        })('AB12');
                    })
                    .join('');
                break;
        }

        return result;
    }

    // * helper method for navigating between tabs
    navigateBack() {
        this.instanceDataService.instanceNavigationHandler(20);
    }

    // * add new line number format
    addNewLineNumberFormat() {
        const indexWithoutId = this.defaultLineNumberFormatList.findIndex((item) => !item.lineNumberFormatId);
        const indexWitName = this.defaultLineNumberFormatList.filter((item) => !item.lineNumberFormatId);
        if (indexWithoutId != -1 && indexWitName?.length > 0 && indexWitName[0].name != 'Default') {
            this.formGroupArray.splice(indexWithoutId, 1);
            this.instanceDataService.lineNumberFormArray = this.formGroupArray;
            this.defaultLineNumberFormatList.splice(indexWithoutId, 1);
        }
        let newLineNumberFormat = new LineNumberFormat();
        newLineNumberFormat.name = `Default ${this.defaultLineNumberFormatList.length + 1}`;
        let newLineNumberFormatSection = new LineNumberFormatSection();
        newLineNumberFormat.sections = [newLineNumberFormatSection];
        let form = this.createFormControls(newLineNumberFormat) as UntypedFormGroup;

        let result: any = {};
        result.name = form.get('name').value;
        result.allowFreeForm = form.get('allowFreeCheck').value;
        result.isDefault = form.get('defaultCheck').value;
        result.sections = form.value.section.map((section, index) => {
            return {
                name: section.name,
                index: index + 1,
                lineNumberFormatSectionTypeId: section.sectionType,
                allowAlpha: section.checkboxGroup.allowAlpha ? section.checkboxGroup.allowAlpha : false,
                allowNumeric: section.checkboxGroup.allowNumeric ? section.checkboxGroup.allowNumeric : false,
                // minLength: section.minMaxGroup.minChar,
                minLength: 0,
                maxLength: section.minMaxGroup.maxChar,
            };
        });

        this.defaultLineNumberFormatList.push(result);
        this.formGroupArray = [...this.formGroupArray, form];
        this.instanceDataService.lineNumberFormArray = this.formGroupArray;

        this.loadNumberFormatDetails(newLineNumberFormat, this.defaultLineNumberFormatList.length - 1);

        form.valueChanges.subscribe((res) => {
            this.updateValidators();
        });
    }

    // * delete line number format
    deleteLineNumberFormat(lineNumberFormat: LineNumberFormat) {
        let dialogRef = this.ngbModal.open(DeleteDialogComponent, {
            size: 'sm',
            keyboard: false,
            backdrop: 'static',
        });
        dialogRef.componentInstance.modalMessage = `<p>Do you want to delete <strong>${lineNumberFormat.name}<strong>? </p>`;
        dialogRef.result.then((res) => {
            if (res) {
                if (lineNumberFormat?.lineNumberFormatId) {
                    let payload = [{ lineNumberFormatId: lineNumberFormat.lineNumberFormatId, active: false }];
                    // * api call
                    this.ngxUiLoaderService.start();
                    this.managementApiService.saveLineNumberFormat(this.activatedRoute.snapshot.paramMap.get('id'), payload).subscribe(
                        (result) => {
                            this.ngxUiLoaderService.stop();
                            if (result.success) {
                                let index = this.defaultLineNumberFormatList.findIndex(
                                    (item) => item.lineNumberFormatId == lineNumberFormat.lineNumberFormatId
                                );
                                if (this.selectedLineFormat?.lineNumberFormatId == lineNumberFormat.lineNumberFormatId) this.selectedLineFormat = null;

                                this.defaultLineNumberFormatList.splice(index, 1);
                                this.formGroupArray.splice(index, 1);
                                this.instanceDataService.lineNumberFormArray = this.formGroupArray;
                            } else if (result.isCustom) {
                                this.userUtilityService.errorModelPopup(result.message);
                            }
                        },
                        (err) => {
                            this.ngxUiLoaderService.stop();
                        }
                    );
                } else {
                    let index = this.defaultLineNumberFormatList.findIndex((item) => item.name.toLowerCase().replace(' ', '') == lineNumberFormat.name);
                    this.defaultLineNumberFormatList.splice(index, 1);
                    this.formGroupArray.splice(index, 1);
                    this.selectedLineFormat = null;
                }
            }
        });
    }

    // * generate api payload
    generateApiPayload() {
        this.apiPayLoad = [];
        this.formGroupArray.forEach((item) => {
            let currentLineNumberFormat: any = {};
            if (item.dirty) {
                if (item.get('lineNumberFormatId').value) {
                    currentLineNumberFormat =
                        this.defaultLineNumberFormatList[
                            this.defaultLineNumberFormatList.findIndex((format) => format.lineNumberFormatId == item.value.lineNumberFormatId)
                        ];
                } else {
                    currentLineNumberFormat =
                        this.defaultLineNumberFormatList[
                            this.defaultLineNumberFormatList.findIndex(
                                (format) => format.name.toLowerCase().replace(' ', '') == item.value.name.toLowerCase().replace(' ', '')
                            )
                        ];
                }
                currentLineNumberFormat.name = item.get('name').value;
                currentLineNumberFormat.isDefault = item.get('defaultCheck').value;
                currentLineNumberFormat.allowFreeForm = item.get('allowFreeCheck').value;
                if (item.get('lineNumberFormatId').value) currentLineNumberFormat.lineNumberFormatId = item.get('lineNumberFormatId').value;
                if (item.value.section.length) {
                    currentLineNumberFormat.sections = item.value.section.map((section, index) => {
                        if (section.lineNumberFormatSectionId) {
                            return {
                                lineNumberFormatSectionId: section.lineNumberFormatSectionId,
                                index: index + 1,
                                name: section.name,
                                lineNumberFormatSectionTypeId: section.sectionType,
                                allowAlpha: section.checkboxGroup?.allowAlpha,
                                allowNumeric: section.checkboxGroup?.allowNumeric,
                                minLength: section.minMaxGroup?.minChar,
                                maxLength: section.minMaxGroup?.maxChar,
                            };
                        } else {
                            return {
                                name: section.name,
                                index: index + 1,
                                lineNumberFormatSectionTypeId: section?.sectionType,
                                allowAlpha: section.checkboxGroup?.allowAlpha,
                                allowNumeric: section.checkboxGroup?.allowNumeric,
                                minLength: section.minMaxGroup?.minChar,
                                maxLength: section.minMaxGroup?.maxChar,
                            };
                        }
                    });
                }
                this.apiPayLoad.push(currentLineNumberFormat);
            }
        });
    }

    // * update form control validators
    updateValidators() {
        // TODO Need to implement the same validation logic for Line Number Format Name
        let lineNumberNameList = this.formGroupArray.map((res) => res.value.name);
        this.formGroupArray.forEach((formGroup, index) => {
            let nameControl = formGroup.get('name');
            let targetedNameList = lineNumberNameList.slice().filter((name, pos) => pos !== index);
            if (this.compareValues(targetedNameList, nameControl.value)) {
                nameControl.setErrors({ duplicateName: true });
            } else {
                nameControl.setErrors(null);
                if (!nameControl.value) nameControl.setErrors({ required: true });
            }
        });

        this.checkDefaultChecked();

        this.formGroupArray.forEach((mainFormGroup) => {
            const sectionFormArray = mainFormGroup.get('section') as UntypedFormArray;
            sectionFormArray.controls.forEach((sectionGroup, index) => {
                const sectionNameList = sectionFormArray.controls.filter((group, pos) => index !== pos).map((group) => group.value.name);

                if (this.compareValues(sectionNameList, sectionGroup.value.name)) {
                    sectionGroup.get('name').setErrors({ duplicateName: true });
                } else {
                    sectionGroup.get('name').setErrors(null);
                    if (!sectionGroup.get('name').value) sectionGroup.get('name').setErrors({ required: true });
                }
            });
        });

        this.cd.detectChanges();
    }

    checkDefaultChecked() {
        let lineNumberDefaultList = this.formGroupArray.map((res) => res.value.defaultCheck);
        this.formGroupArray.forEach((formGroup) => {
            let defaultCheckControl = formGroup.get('defaultCheck');
            if (lineNumberDefaultList.filter((t) => t === true).length > 1) {
                defaultCheckControl.setErrors({ alreadyDefaultSelected: true });
            } else if (lineNumberDefaultList.every((t) => t === false)) {
                defaultCheckControl.setErrors({ oneRequireDefaultSelected: true });
            } else {
                defaultCheckControl.setErrors(null);
            }
        });
    }

    // * set particular form Group Validations
    updateFromGroupValidators(index: number): void {
        let lineNumberNameList = this.formGroupArray.map((res) => res.value.name);
        let targetedNameList = lineNumberNameList.slice().filter((item, pos) => pos !== index);
        let nameControl = this.formGroupArray[index].get('name');
        if (this.compareValues(targetedNameList, nameControl.value)) {
            nameControl.setErrors({ duplicateName: true });
        } else {
            nameControl.setErrors(null);
            if (!nameControl.value) nameControl.setErrors({ required: true });
        }

        this.checkDefaultChecked();
        const sectionFormArray = this.formGroupArray[index].get('section') as UntypedFormArray;
        sectionFormArray.controls.forEach((sectionGroup, ind) => {
            const sectionNameList = sectionFormArray.controls.filter((group, pos) => ind !== pos).map((group) => group.value.name);
            if (this.compareValues(sectionNameList, sectionGroup.value.name)) {
                sectionGroup.get('name').setErrors({ duplicateName: true });
            } else {
                sectionGroup.get('name').setErrors(null);
                if (!sectionGroup.get('name').value) sectionGroup.get('name').setErrors({ required: true });
            }
        });
    }

    // * helper method to compare the values of form controls
    compareValues(namesList: string[], targetValue: string): boolean {
        return namesList.find((name) => name.toLowerCase().trim() === targetValue.toLowerCase().trim()) ? true : false;
    }

    // * helper method to get the formGroup
    getFormGroup() {
        let index = this.defaultLineNumberFormatList.findIndex(
            (item) => item.name.toLowerCase().replace(' ', '').trim() == this.selectedLineFormat.name.toLowerCase().replace(' ', '').trim()
        );
        return this.formGroupArray[index];
    }

    // * create Joint ID form
    createJointIdForm(setting: InstanceSettings) {
        this.jointIDForm = this.fromBuilder.group({
            jointIdType: new UntypedFormControl(setting.settingValue.settingValueId, Validators.required),
            jointIDPrefix: new UntypedFormControl('', []),
        });

        this.instanceDataService.jointIDForm = this.jointIDForm;
    }

    // * helper method to handle the next button disabled
    handleDisable() {
        return !(this.formGroupArray.every((item) => item.valid) && this.formGroupArray.some((x) => x.dirty)) || !this.jointIDForm?.valid;
    }

    // * tab changes event handler
    handleTabChange(event) {
        this.activeIndex = event;
    }

    allowFreeFormHandler(event) {
        if (event) {
            let index = this.defaultLineNumberFormatList.findIndex((x) => x.name == event.name);
            let group = this.formGroupArray[index];
            this.defaultLineNumberFormatList[index].sections = [];
            this.defaultLineNumberFormatList[index].allowFreeForm = group.get('allowFreeCheck').value;
        }
    }

    formetNameChangedHander(event): void {
        if (event) {
            this.defaultLineNumberFormatList[this.selectedLineNumberIndex].name = event;
            this.selectedLineFormat.name = event;
        }
    }

    // * helper method to load the joint id
    loadJointID() {
        this.selectedLineFormat = null;
        this.isJointFormatSelect = true;
        // TODO assign necessary properties
    }
    async cancelClick() {
        // this.formGroupArray = [];
        this.apiPayLoad = [];
        this.selectedLineFormat = null;
        this.isJointFormatSelect = false;

        this.formGroupArray.map((x) => {
            if (x) {
                x.markAsPristine();
                x.markAsUntouched();
            }
        });
        let tt = this.defaultLineNumberFormatList;
        let tte = this.formGroupArray;
        const indexWithoutId = this.defaultLineNumberFormatList.findIndex((item) => !item.lineNumberFormatId);
        if (indexWithoutId != -1) {
            this.formGroupArray.splice(indexWithoutId, 1);
            this.instanceDataService.lineNumberFormArray = this.formGroupArray;
            this.defaultLineNumberFormatList.splice(indexWithoutId, 1);
        }
        this.jointIDForm.reset(this.jointIDForm.value);
        await this.getInstanceSettings();
    }
    ngOnDestroy() {
        this.destroy$.next(null);
        this.destroy$.complete();
    }
}
